import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  ListResponse,
  PriceDto,
  PriceListDetailDto,
  PriceListDto,
  PriceRowPreviewDto,
} from "@reina/shared";
import type { AmendPriceInput, PastePriceListInput } from "@reina/shared";
import { api } from "./client";
import { uploadViaPresign } from "../lib/uploads";

/**
 * TanStack Query hooks for price-list ingest + SCD2 prices (contract §Price
 * lists & prices). Key layout follows api/catalogue.ts: `["price-lists", ...]`
 * so one invalidation sweeps list/detail/rows/counts. Parsing runs async on
 * the server, so list/detail queries poll every 2s while anything is parsing.
 */

const PriceListList = ListResponse(PriceListDto);
const PriceRowList = ListResponse(PriceRowPreviewDto);

/** Both upload and paste respond `{priceList}`. */
const PriceListEnvelope = z.object({ priceList: PriceListDto });

/** Publish responds `{priceList}` + SCD2 transaction counts. */
const PublishResult = z.object({
  priceList: PriceListDto,
  inserted: z.number().int(),
  closed: z.number().int(),
  unchanged: z.number().int(),
});
export type PublishResult = z.infer<typeof PublishResult>;

const AmendResult = z.object({ price: PriceDto });

const POLL_MS = 2000;

export type PriceListListParams = {
  supplierId?: string;
  status?: string;
  limit?: number;
  offset?: number;
};

export function usePriceLists(params: PriceListListParams = { limit: 500 }) {
  return useQuery({
    queryKey: ["price-lists", "list", params],
    queryFn: () => api.get("/price-lists", { query: params, schema: PriceListList }),
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((list) => list.status === "parsing") ? POLL_MS : false,
  });
}

export function usePriceList(id: string) {
  return useQuery({
    queryKey: ["price-lists", "detail", id],
    queryFn: () => api.get(`/price-lists/${id}`, { schema: PriceListDetailDto }),
    refetchInterval: (query) => (query.state.data?.status === "parsing" ? POLL_MS : false),
  });
}

export function usePriceListRows(
  id: string,
  params: { limit: number; offset: number },
  /** Poll while the list is still parsing so staged rows stream in. */
  polling = false
) {
  return useQuery({
    queryKey: ["price-lists", "rows", id, params],
    queryFn: () => api.get(`/price-lists/${id}/rows`, { query: params, schema: PriceRowList }),
    refetchInterval: polling ? POLL_MS : false,
  });
}

export interface UploadPriceListInput {
  file: File;
  supplierId: string;
  effectiveDate?: string;
}

export function useUploadPriceList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, supplierId, effectiveDate }: UploadPriceListInput) => {
      // Presign → direct PUT to storage → register by key (files never squeeze
      // through the API process; on Lambda they couldn't).
      const { uploadKey, filename } = await uploadViaPresign(file, "price_list");
      return api.post(
        "/price-lists/from-key",
        { supplierId, uploadKey, filename, ...(effectiveDate ? { effectiveDate } : {}) },
        { schema: PriceListEnvelope },
      );
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["price-lists"] }),
  });
}

export function usePastePriceList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PastePriceListInput) =>
      api.post("/price-lists/paste", input, { schema: PriceListEnvelope }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["price-lists"] }),
  });
}

export function usePublishPriceList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post(`/price-lists/${id}/publish`, undefined, { schema: PublishResult }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["price-lists"] });
      // Publish creates products/prices and recomputes comparable lows.
      void queryClient.invalidateQueries({ queryKey: ["products"] });
      void queryClient.invalidateQueries({ queryKey: ["comparable-groups"] });
    },
  });
}

export function useAmendPrice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ priceId, input }: { priceId: string; input: AmendPriceInput }) =>
      api.post(`/prices/${priceId}/amend`, input, { schema: AmendResult }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["price-lists"] });
      void queryClient.invalidateQueries({ queryKey: ["comparable-groups"] });
    },
  });
}
